import { FC, useState } from 'react';
import { Alert, Box, IconButton, Popover, Typography } from '@mui/material';
import { ChevronRight, Close, Tune } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AIModelSelectionType } from '../../../../context/MannequinContext';
import {
    getOutputFormatLabel,
    shouldShowNextGenerationFormatHint,
} from '../../AIGeneration.helper';
import { HangerCardOutputConfig, getHangerCardOutputSummary } from './HangerCardOutputConfig';
import './HangerCardOutputConfigPopover.scss';

interface HangerCardOutputConfigPopoverProps {
    selectedModel: AIModelSelectionType;
    resolution: string;
    aspectRatio: string;
    outputFormat: string;
    generatedOutputFormat?: string;
    quality?: string;
    outputFormatDisabled?: boolean;
    onResolutionChange: (value: string) => void;
    onAspectRatioChange: (value: string) => void;
    onOutputFormatChange: (value: string) => void;
    onQualityChange?: (value: string) => void;
}

export const HangerCardOutputConfigPopover: FC<HangerCardOutputConfigPopoverProps> = ({
    selectedModel,
    resolution,
    aspectRatio,
    outputFormat,
    generatedOutputFormat,
    quality,
    outputFormatDisabled,
    onResolutionChange,
    onAspectRatioChange,
    onOutputFormatChange,
    onQualityChange,
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const summary = getHangerCardOutputSummary(selectedModel, aspectRatio, resolution);
    const showNextGenerationFormatHint = shouldShowNextGenerationFormatHint(
        outputFormat,
        generatedOutputFormat
    );
    const outputFormatChangeHint = showNextGenerationFormatHint
        ? t('mannequin.aiGeneration.hangerFlow.uploadGenerate.outputFormatNextGenerationHint', {
              format: getOutputFormatLabel(outputFormat),
          })
        : undefined;

    return (
        <Box className="hanger-output-config-popover">
            <button
                type="button"
                className="hanger-output-config-popover__trigger"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                aria-expanded={open}
                aria-haspopup="dialog"
            >
                <Tune className="hanger-output-config-popover__trigger-icon" fontSize="small" />
                <Box className="hanger-output-config-popover__trigger-text">
                    <Typography variant="body2" className="hanger-output-config-popover__trigger-title">
                        {t('mannequin.aiGeneration.configuration.outputSettings.title', 'Output')}
                    </Typography>
                    <Typography variant="caption" className="hanger-output-config-popover__trigger-summary">
                        {summary}
                    </Typography>
                </Box>
                <ChevronRight className="hanger-output-config-popover__trigger-chevron" fontSize="small" />
            </button>

            {outputFormatChangeHint && (
                <Alert severity="info" className="hanger-output-config-popover__format-hint">
                    {outputFormatChangeHint}
                </Alert>
            )}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        className: 'hanger-output-config-popover__paper',
                    },
                }}
            >
                <Box className="hanger-output-config-popover__header">
                    <Typography variant="subtitle2">
                        {t('mannequin.aiGeneration.configuration.outputSettings.title', 'Output')}
                    </Typography>
                    <IconButton size="small" onClick={() => setAnchorEl(null)} aria-label={t('general.close', 'Close')}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>

                <HangerCardOutputConfig
                    variant="popover"
                    selectedModel={selectedModel}
                    resolution={resolution}
                    aspectRatio={aspectRatio}
                    outputFormat={outputFormat}
                    outputFormatChangeHint={outputFormatChangeHint}
                    quality={quality}
                    outputFormatDisabled={outputFormatDisabled}
                    onResolutionChange={onResolutionChange}
                    onAspectRatioChange={onAspectRatioChange}
                    onOutputFormatChange={onOutputFormatChange}
                    onQualityChange={onQualityChange}
                />
            </Popover>
        </Box>
    );
};
